/**
 * Pure HTML-string renderer for the whole app.
 *
 * Re-renders are cheap (no DOM diffing) - after each render `main.ts` repaints
 * the preview canvases and the simulator thumbnail from the store.
 */

import { COMPLIANCE_THRESHOLDS, type AuditReport } from '../utils/image-auditor.ts';
import {
  MARKETPLACE_PRESETS,
  PRESET_ORDER,
  checkBatchQuota,
  extensionForFormat,
  generateBatchFilename
} from '../utils/batch-exporter.ts';
import { FREE_BATCH_LIMIT } from '../utils/license.ts';
import { buildSimulatorMarkup, escapeHtml } from './simulator.ts';
import type { AppState, ProductImage } from './store.ts';

type Tone = 'good' | 'warn' | 'bad';

const TONE_TEXT: Record<Tone, string> = {
  good: 'text-emerald-600',
  warn: 'text-amber-600',
  bad: 'text-rose-600'
};

const TONE_BG: Record<Tone, string> = {
  good: 'bg-emerald-500',
  warn: 'bg-amber-500',
  bad: 'bg-rose-500'
};

const TONE_CHIP: Record<Tone, string> = {
  good: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  warn: 'bg-amber-50 text-amber-700 ring-amber-200',
  bad: 'bg-rose-50 text-rose-700 ring-rose-200'
};

function toneOf(value: number, goodAt: number, warnAt: number): Tone {
  if (value >= goodAt) return 'good';
  if (value >= warnAt) return 'warn';
  return 'bad';
}

export const LOGO_SVG = `<svg viewBox="0 0 32 32" class="h-8 w-8" aria-hidden="true">
      <defs>
        <linearGradient id="asg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#818cf8" />
          <stop offset="100%" stop-color="#22d3ee" />
        </linearGradient>
      </defs>
      <path d="M16 1.8 28.4 6.6v9.1c0 7.7-5.3 13.1-12.4 14.6C8.9 28.8 3.6 23.4 3.6 15.7V6.6z" fill="url(#asg)" />
      <path d="m10.4 15.9 3.7 3.8 7.5-8" fill="none" stroke="#0b1120" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`;

function meter(value: number, tone: Tone): string {
  const pct = Math.max(0, Math.min(100, value));
  return `<div class="score-track"><div class="score-fill ${TONE_BG[tone]}" style="width:${pct}%"></div></div>`;
}

function verdictChip(report: AuditReport): string {
  const tone: Tone = report.isAmazonCompliant ? 'good' : report.perimeterWhiteScore < COMPLIANCE_THRESHOLDS.perimeterWhiteMin || report.fillRatio < COMPLIANCE_THRESHOLDS.fillRatioMin ? 'bad' : 'warn';
  const label = report.isAmazonCompliant ? 'Algorithm-safe' : 'Suppression risk';
  return `<span class="rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${TONE_CHIP[tone]}">${label}</span>`;
}

/* -------------------------------------------------------------------------- */

function headerHtml(state: AppState): string {
  const licenseForm = state.isPro
    ? `<span class="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1.5 text-xs font-bold text-emerald-300 ring-1 ring-emerald-400/40">★ PRO unlocked</span>`
    : `<form class="flex items-center gap-2" data-form="license">
        <label class="sr-only" for="license-key">Pro license key</label>
        <input
          id="license-key"
          name="licenseKey"
          type="text"
          autocomplete="off"
          spellcheck="false"
          placeholder="AS-PRO-XXXX-XXXX-XXXX-CC"
          value="${escapeHtml(state.licenseInput)}"
          class="w-56 rounded-lg border border-slate-700 bg-slate-950/70 px-3 py-1.5 text-xs tracking-wide text-slate-100 placeholder:text-slate-500 focus:border-indigo-400 focus:outline-none"
        />
        <button type="submit" class="rounded-lg bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-indigo-400">Activate</button>
        <button type="button" data-action="demo-license" class="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-300 transition hover:bg-slate-800">Demo key</button>
      </form>`;

  return `<header class="sticky top-0 z-30 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
    <div class="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-4 py-3 sm:px-6">
      <div class="flex items-center gap-3">
        ${LOGO_SVG}
        <div>
          <h1 class="text-sm font-extrabold tracking-tight text-white sm:text-base">AlgorithmShield</h1>
          <p class="hidden text-[11px] text-slate-400 sm:block">Marketplace suppression audit · 100% client-side</p>
        </div>
      </div>
      <div class="ml-auto flex flex-wrap items-center gap-2">
        <span class="rounded-full bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-300 ring-1 ring-slate-700">
          ${state.images.length} image${state.images.length === 1 ? '' : 's'} · free limit ${FREE_BATCH_LIMIT}
        </span>
        ${state.isPro ? '' : `<span class="hidden rounded-full bg-indigo-500/15 px-3 py-1.5 text-xs font-semibold text-indigo-300 ring-1 ring-indigo-400/40 sm:inline">Freemium</span>`}
        ${licenseForm}
      </div>
    </div>
    ${state.licenseMessage ? `<div class="border-t border-slate-800 bg-slate-900/80 px-4 py-2 text-center text-xs ${state.licenseValid ? 'text-emerald-300' : 'text-rose-300'}">${escapeHtml(state.licenseMessage)}</div>` : ''}
  </header>`;
}

function dropzoneHtml(state: AppState): string {
  return `<section class="mx-auto max-w-7xl px-4 pt-8 sm:px-6">
    <div class="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <label
        class="dropzone flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-300 bg-white px-6 py-10 text-center hover:border-indigo-400"
        data-dropzone
      >
        <input type="file" accept="image/*" multiple class="sr-only" data-input="files" />
        <span class="grid h-12 w-12 place-items-center rounded-full bg-indigo-50 text-2xl" aria-hidden="true">📸</span>
        <span class="text-sm font-semibold text-slate-800">Drop product photos or click to browse</span>
        <span class="text-xs text-slate-500">JPG, PNG, WebP · audited locally, nothing is uploaded to any server</span>
      </label>

      <div class="rounded-2xl border border-slate-200 bg-white p-5">
        <h2 class="text-xs font-bold uppercase tracking-wider text-slate-500">Algorithm checks</h2>
        <ul class="mt-3 space-y-3 text-sm">
          <li class="flex items-start gap-2"><span class="mt-0.5 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold text-white">01</span><span><strong>Pure white perimeter</strong> — % of border pixels exactly <code class="rounded bg-slate-100 px-1">#FFFFFF</code> (target ≥ ${COMPLIANCE_THRESHOLDS.perimeterWhiteMin}%)</span></li>
          <li class="flex items-start gap-2"><span class="mt-0.5 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold text-white">02</span><span><strong>Product frame fill</strong> — non-white bounding box vs canvas (Amazon wants ≥ ${COMPLIANCE_THRESHOLDS.fillRatioMin}%)</span></li>
          <li class="flex items-start gap-2"><span class="mt-0.5 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold text-white">03</span><span><strong>Mobile thumbnail pop</strong> — 150×150 RMS contrast + edge sharpness (target ≥ ${COMPLIANCE_THRESHOLDS.popScoreMin})</span></li>
        </ul>
        <p class="mt-4 text-[11px] leading-relaxed text-slate-500">Pro license unlocks auto 86% bounding-box fill scaling and 100+ image batch ZIP export.</p>
      </div>
    </div>
    ${state.images.length > FREE_BATCH_LIMIT && !state.isPro ? quotaBanner(state.images.length) : ''}
  </section>`;
}

function quotaBanner(count: number): string {
  return `<div class="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
    <span class="font-bold">Free batch limit:</span>
    <span>${count} images loaded — exports are limited to ${FREE_BATCH_LIMIT} images per batch until Pro is activated.</span>
    <button type="button" data-action="focus-license" class="ml-auto rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-500">Enter license key</button>
  </div>`;
}

/* -------------------------------------------------------------------------- */

function metricBlock(options: {
  index: string;
  title: string;
  value: string;
  tone: Tone;
  valuePct: number;
  hint: string;
  footer: string;
}): string {
  return `<div class="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
      <div class="flex items-baseline justify-between gap-2">
        <span class="text-[11px] font-bold uppercase tracking-wider text-slate-500">${options.index} · ${options.title}</span>
        <span class="text-lg font-extrabold tabular-nums ${TONE_TEXT[options.tone]}">${options.value}</span>
      </div>
      <div class="mt-2">${meter(options.valuePct, options.tone)}</div>
      <p class="mt-2 text-[11px] leading-snug text-slate-500">${options.hint}</p>
      ${options.footer ? `<div class="mt-2.5">${options.footer}</div>` : ''}
    </div>`;
}

function imageCardHtml(image: ProductImage, state: AppState): string {
  const { report } = image;
  const isActive = state.activeId === image.id || (state.activeId === null && state.images[0]?.id === image.id);

  const perimeterTone = toneOf(report.perimeterWhiteScore, COMPLIANCE_THRESHOLDS.perimeterWhiteMin, 80);
  const fillTone = toneOf(report.fillRatio, COMPLIANCE_THRESHOLDS.fillRatioMin, 60);
  const popTone = toneOf(report.popScore, COMPLIANCE_THRESHOLDS.popScoreMin, 30);

  const snapDisabled = report.perimeterWhiteScore >= 100 ? 'disabled' : '';
  const snapFooter = `<button type="button" data-action="snap-white" data-id="${image.id}" ${snapDisabled} class="w-full rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300">Snap near-white (&gt;240) → #FFFFFF</button>`;

  const autoFillFooter = state.isPro
    ? `<button type="button" data-action="auto-fill" data-id="${image.id}" class="w-full rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-indigo-500">${image.autoFilled ? 'Re-apply exact 86% fill' : 'Auto-crop & pad to exact 86% fill'}</button>`
    : `<button type="button" data-action="lock-pro" class="w-full rounded-lg border border-dashed border-indigo-300 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-500 transition hover:bg-indigo-100">🔒 Auto-crop & pad to 86% — Pro</button>`;

  return `<article class="card-in flex flex-col gap-3 rounded-2xl border ${isActive ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-slate-200'} bg-white p-4 shadow-sm" data-image-card="${image.id}">
    <div class="flex items-start justify-between gap-2">
      <div class="min-w-0">
        <h3 class="truncate text-sm font-bold text-slate-800" title="${escapeHtml(image.fileName)}">${escapeHtml(image.fileName)}</h3>
        <p class="text-[11px] text-slate-500">${image.workingWidth}×${image.workingHeight}px${image.snapped ? ' · white snapped' : ''}${image.autoFilled ? ' · 86% fill' : ''}</p>
      </div>
      ${verdictChip(report)}
    </div>

    <div class="relative overflow-hidden rounded-xl border border-slate-200 bg-[repeating-conic-gradient(#f8fafc_0%_25%,#eef2f7_0%_50%)] bg-[length:16px_16px]">
      <canvas data-preview="${image.id}" class="mx-auto block h-auto max-h-52 w-full object-contain"></canvas>
    </div>

    ${metricBlock({
      index: '01',
      title: 'White perimeter',
      value: `${report.perimeterWhiteScore.toFixed(1)}%`,
      tone: perimeterTone,
      valuePct: report.perimeterWhiteScore,
      hint: `Border pixels that are strictly RGB(255,255,255). Target ≥ ${COMPLIANCE_THRESHOLDS.perimeterWhiteMin}%.`,
      footer: snapFooter
    })}

    ${metricBlock({
      index: '02',
      title: 'Frame fill ratio',
      value: `${report.fillRatio.toFixed(1)}%`,
      tone: fillTone,
      valuePct: report.fillRatio,
      hint: `Product box (${report.boundingBox.width}×${report.boundingBox.height}px) vs canvas. Target ≥ ${COMPLIANCE_THRESHOLDS.fillRatioMin}%.`,
      footer: autoFillFooter
    })}

    ${metricBlock({
      index: '03',
      title: 'Mobile pop score',
      value: `${report.popScore}`,
      tone: popTone,
      valuePct: report.popScore,
      hint: `150×150 downsample · RMS contrast ${report.rmsContrast} · edge sharpness ${report.edgeSharpness}. Target ≥ ${COMPLIANCE_THRESHOLDS.popScoreMin}.`,
      footer: ''
    })}

    <div class="mt-auto flex gap-2 pt-1">
      <button type="button" data-action="select" data-id="${image.id}" class="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">Show in grid</button>
      <button type="button" data-action="reset" data-id="${image.id}" class="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">Reset</button>
      <button type="button" data-action="remove" data-id="${image.id}" class="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-50" aria-label="Remove ${escapeHtml(image.fileName)}">✕</button>
    </div>
  </article>`;
}

function imagesSectionHtml(state: AppState): string {
  if (state.images.length === 0) {
    return `<section class="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div class="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-12 text-center">
        <p class="text-sm font-semibold text-slate-600">No images yet — add up to ${FREE_BATCH_LIMIT} photos to see live audit scores.</p>
      </div>
    </section>`;
  }

  const passing = state.images.filter((image) => image.report.isAmazonCompliant).length;
  const avgPop = Math.round(state.images.reduce((sum, image) => sum + image.report.popScore, 0) / state.images.length);
  const avgFill = (state.images.reduce((sum, image) => sum + image.report.fillRatio, 0) / state.images.length).toFixed(1);

  return `<section class="mx-auto max-w-7xl px-4 py-8 sm:px-6">
    <div class="mb-4 flex flex-wrap items-center gap-3">
      <h2 class="text-base font-extrabold tracking-tight text-slate-900">Audit results</h2>
      <div class="flex flex-wrap gap-2 text-xs">
        <span class="rounded-full bg-white px-3 py-1 font-semibold text-slate-700 ring-1 ring-slate-200">${passing}/${state.images.length} compliant</span>
        <span class="rounded-full bg-white px-3 py-1 font-semibold text-slate-700 ring-1 ring-slate-200">avg fill ${avgFill}%</span>
        <span class="rounded-full bg-white px-3 py-1 font-semibold text-slate-700 ring-1 ring-slate-200">avg pop ${avgPop}</span>
      </div>
      <div class="ml-auto flex gap-2">
        <button type="button" data-action="snap-all" class="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-700">Snap near-white in all</button>
        <button type="button" data-action="clear" class="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50">Clear batch</button>
      </div>
    </div>
    <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
      ${state.images.map((image) => imageCardHtml(image, state)).join('')}
    </div>
  </section>`;
}

/* -------------------------------------------------------------------------- */

function simulatorSectionHtml(state: AppState): string {
  const hasActive = state.images.length > 0;
  return `<section class="border-t border-slate-200 bg-gradient-to-b from-slate-100 to-slate-50 py-10">
    <div class="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[1fr_auto]">
      <div class="max-w-xl">
        <span class="text-xs font-bold uppercase tracking-widest text-indigo-600">Live mobile search grid</span>
        <h2 class="mt-2 text-2xl font-extrabold tracking-tight text-slate-900">See your photo the way shoppers do</h2>
        <p class="mt-3 text-sm leading-relaxed text-slate-600">
          Your product is rendered at the exact <strong>150×150px</strong> mobile thumbnail size inside a
          two-column Amazon-style search feed, next to five sample competitors with real badges, ratings
          and prices. If your card looks washed out or the product looks tiny, the algorithm agrees.
        </p>
        <ul class="mt-4 space-y-2 text-sm text-slate-600">
          <li>• Toggle <strong>Light / Dark mode</strong> to check contrast on both shopper themes.</li>
          <li>• Move your listing from <strong>Position #1 to #6</strong> to test first-impression impact.</li>
          <li>• White background is preserved - marketplaces require it, so the sim does not hide it.</li>
        </ul>
        ${hasActive ? '' : `<p class="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">Add an image to place your listing in the grid.</p>`}
      </div>
      <div>
        ${buildSimulatorMarkup({ simSlot: state.simSlot, theme: state.simTheme, hasActiveImage: hasActive })}
      </div>
    </div>
  </section>`;
}

/* -------------------------------------------------------------------------- */

function exportSectionHtml(state: AppState): string {
  const quota = checkBatchQuota(state.images.length, state.isPro);
  const ext = extensionForFormat(state.format);
  const skuValue = escapeHtml(state.sku);
  const preview = state.images.length > 0 ? generateBatchFilename(state.sku, 1, ext) : `${escapeHtml(state.sku || 'product')}-main-01.${ext}`;

  const presetButtons = PRESET_ORDER.map((id) => {
    const preset = MARKETPLACE_PRESETS[id];
    const selected = state.preset === id;
    return `<button
        type="button"
        data-action="set-preset"
        data-preset="${id}"
        class="flex-1 rounded-xl border px-3 py-3 text-left transition ${
          selected
            ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
            : 'border-slate-200 bg-white hover:border-slate-300'
        }"
      >
        <span class="block text-xs font-extrabold ${selected ? 'text-indigo-700' : 'text-slate-800'}">${preset.platform}</span>
        <span class="block text-[11px] tabular-nums text-slate-500">${preset.width}×${preset.height}</span>
      </button>`;
  }).join('');

  const formatButtons = (['image/jpeg', 'image/webp'] as const)
    .map((format) => {
      const selected = state.format === format;
      const label = format === 'image/jpeg' ? 'JPEG (.jpg)' : 'WebP (.webp)';
      return `<button
        type="button"
        data-action="set-format"
        data-format="${format}"
        class="flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
          selected ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-200' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
        }"
      >${label}</button>`;
    })
    .join('');

  const exportDisabled = state.exporting || state.images.length === 0 || !quota.allowed;
  const quotaNote = !quota.allowed
    ? `<p class="text-xs font-semibold text-rose-600">${escapeHtml(quota.message ?? '')}</p>`
    : `<p class="text-xs text-slate-500">${state.images.length} image${state.images.length === 1 ? '' : 's'} → ${state.images.length}-file ZIP · EXIF stripped · sequential SKU names</p>`;

  return `<section id="export" class="mx-auto max-w-7xl px-4 py-10 sm:px-6">
    <div class="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
      <div class="rounded-2xl border border-slate-200 bg-white p-6">
        <span class="text-xs font-bold uppercase tracking-widest text-indigo-600">Batch SKU renamer & exporter</span>
        <h2 class="mt-2 text-xl font-extrabold tracking-tight text-slate-900">Export marketplace-ready ZIP</h2>
        <p class="mt-2 text-sm text-slate-600">Every image is re-encoded through Canvas (strips EXIF), resized to the preset, renamed sequentially and zipped locally.</p>

        <div class="mt-5 grid gap-4 sm:grid-cols-2">
          <div>
            <label for="sku-input" class="mb-1.5 block text-xs font-bold text-slate-700">SEO SKU slug</label>
            <input id="sku-input" type="text" value="${skuValue}" data-input="sku" placeholder="ceramic-vase"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none" />
            <p class="mt-1.5 text-[11px] text-slate-500">First file: <code class="rounded bg-slate-100 px-1 font-semibold text-slate-700">${preview}</code></p>
          </div>
          <div>
            <span class="mb-1.5 block text-xs font-bold text-slate-700">Format</span>
            <div class="flex gap-2">${formatButtons}</div>
            <label for="quality-input" class="mb-1.5 mt-3 flex items-center justify-between text-xs font-bold text-slate-700">
              <span>Quality</span><span class="tabular-nums text-slate-500" data-quality-label>${Math.round(state.quality * 100)}%</span>
            </label>
            <input id="quality-input" type="range" min="0.5" max="1" step="0.01" value="${state.quality}" data-input="quality"
              class="w-full accent-indigo-600" />
          </div>
        </div>

        <div class="mt-5">
          <span class="mb-1.5 block text-xs font-bold text-slate-700">Marketplace preset</span>
          <div class="flex gap-3">${presetButtons}</div>
        </div>

        <div class="mt-6 flex flex-wrap items-center gap-3">
          <button type="button" data-action="export-zip" ${exportDisabled ? 'disabled' : ''}
            class="rounded-xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-300">
            ${state.exporting ? 'Building ZIP…' : `Download ZIP (${state.images.length})`}
          </button>
          ${quotaNote}
        </div>
      </div>

      <div class="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 class="text-sm font-extrabold text-slate-800">What the exporter does</h3>
        <ol class="mt-3 space-y-3 text-sm text-slate-600">
          <li class="flex gap-2"><span class="font-bold text-slate-900">1.</span> Re-encode via HTML5 Canvas → camera EXIF (orientation, GPS, model) removed.</li>
          <li class="flex gap-2"><span class="font-bold text-slate-900">2.</span> Resize with a box filter to ${MARKETPLACE_PRESETS[state.preset].width}×${MARKETPLACE_PRESETS[state.preset].height} on pure white padding.</li>
          <li class="flex gap-2"><span class="font-bold text-slate-900">3.</span> Rename sequentially: <code class="rounded bg-slate-100 px-1 text-xs">${preview.replace('01', '02')}</code> …</li>
          <li class="flex gap-2"><span class="font-bold text-slate-900">4.</span> Pack into a client-side ZIP (fflate) — no upload, no server.</li>
        </ol>
        <div class="mt-5 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <p class="text-xs font-bold text-slate-700">${state.isPro ? 'Pro plan active' : 'Free plan'}</p>
          <p class="mt-1 text-xs leading-relaxed text-slate-500">
            ${state.isPro
              ? `Batch ZIP export up to 1000 images and auto 86% bounding-box fill scaling enabled.`
              : `Up to ${FREE_BATCH_LIMIT} images per batch. Activate a Pro key for 100+ batch ZIP export and auto 86% fill scaling.`}
          </p>
        </div>
      </div>
    </div>
  </section>`;
}

function footerHtml(): string {
  return `<footer class="border-t border-slate-200 bg-white">
    <div class="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-xs text-slate-500 sm:px-6">
      <span>AlgorithmShield — progressive web app. Your photos never leave this device.</span>
      <span>Vite · TypeScript · Tailwind CSS · Canvas · fflate</span>
    </div>
  </footer>`;
}

function toastHtml(state: AppState): string {
  if (!state.toast) return '';
  const styles = {
    success: 'bg-emerald-600 text-white',
    error: 'bg-rose-600 text-white',
    info: 'bg-slate-900 text-white'
  } as const;
  return `<div class="toast-in fixed bottom-5 right-5 z-50 max-w-sm rounded-xl px-4 py-3 text-sm font-semibold shadow-xl ${styles[state.toast.kind]}" role="status" data-toast>
    ${escapeHtml(state.toast.text)}
  </div>`;
}

/** Complete page markup for the given state. */
export function renderApp(state: AppState): string {
  return `
    ${headerHtml(state)}
    <main>
      ${dropzoneHtml(state)}
      ${imagesSectionHtml(state)}
      ${simulatorSectionHtml(state)}
      ${exportSectionHtml(state)}
    </main>
    ${footerHtml()}
    ${toastHtml(state)}
  `;
}
